import { Control } from 'react-hook-form'
import {
  Dropdown,
  FormLayout,
  TextInput,
  MultiSelectStoreDropdown,
} from 'components/common'
import SwitchField from 'components/bundleProduct/switchField'
import FtpProfileOverrides from './ftpProfileOverrides'
import FtpRunStatus from './ftpRunStatus'
import {
  CommonFormType,
  FTP_PROTOCOLS,
  aggregatorFormFields,
  ftpProtocolOptions,
  getNestErrorMessage,
} from '../utils'

const {
  FTP_ACTIVE,
  FTP_HOST,
  FTP_PASSWORD,
  FTP_PATH,
  FTP_PORT,
  FTP_PROTOCOL,
  FTP_USERNAME,
  FTP_DELETE_FILES,
  FTP_ENABLE_S3_STORAGE,
  FTP_FILE_GENERATION_LIMIT: FILE_GENERATION_LIMIT,
  FTP_CRON_EXPRESSION,
  FTP_ACTIVATED_STORES,
  FTP_MAX_RUNTIME_MINUTES,
  FTP_MAX_HOURS_WITHOUT_RUN,
} = aggregatorFormFields

const fieldName = (field: { parentKey: string; key: string }) =>
  `${field.parentKey}.${field.key}`

interface SectionHeaderProps {
  title: string
  description?: string
}

const SectionHeader = ({ title, description }: SectionHeaderProps) => (
  <div className="mb-4">
    <p className="text-sm font-bold text-sidebar-primary">{title}</p>
    {description && <p className="text-xs text-gray-500">{description}</p>}
  </div>
)

interface FtpConfigurationFormProps extends CommonFormType {
  control: Control<any>
}

function FtpConfigurationForm({
  register,
  errors,
  setValue,
  control,
  aggregator,
  ftpIsActive,
}: FtpConfigurationFormProps) {
  return (
    <>
      <h3 className="title">FTP Configuration</h3>

      <FormLayout>
        {/* General - applies to the default config and every profile override */}
        <SectionHeader
          title="General"
          description="Applies to the default configuration and every FTP profile override."
        />

        <div className="grid gap-x-10 gap-y-1 mid-md:grid-cols-3">
          <SwitchField
            control={control}
            name={fieldName(FTP_ACTIVE)}
            label={FTP_ACTIVE.label}
            description="Enable or disable this FTP configuration."
            defaultValue={aggregator?.ftp?.isEnable ?? false}
          />

          <SwitchField
            control={control}
            name={fieldName(FTP_DELETE_FILES)}
            label={FTP_DELETE_FILES.label}
            description="Clear existing files before generating new ones."
            defaultValue={aggregator?.ftp?.deleteFiles ?? true}
          />

          <SwitchField
            control={control}
            name={fieldName(FTP_ENABLE_S3_STORAGE)}
            label={FTP_ENABLE_S3_STORAGE.label}
            description="Keep a mirrored copy of every generated file in S3."
            defaultValue={aggregator?.ftp?.enableS3Storage ?? false}
          />
        </div>

        <div className="my-6 border-t" />

        {/* Connection - where files are delivered */}
        <SectionHeader
          title="Connection"
          description="FTP/SFTP server the generated files are uploaded to."
        />

        <div className="grid gap-x-5 gap-y-3 mid-md:grid-cols-3">
          <Dropdown
            required={ftpIsActive}
            setValue={setValue}
            options={ftpProtocolOptions}
            defaultValue={aggregator?.ftp?.protocol || FTP_PROTOCOLS.FTP}
            label={FTP_PROTOCOL.label}
            name={fieldName(FTP_PROTOCOL)}
            error={getNestErrorMessage(errors, FTP_PROTOCOL)}
          />

          <TextInput
            required={ftpIsActive}
            register={register}
            label={FTP_HOST.label}
            name={fieldName(FTP_HOST)}
            error={getNestErrorMessage(errors, FTP_HOST)}
          />

          <TextInput
            required={ftpIsActive}
            register={register}
            label={FTP_PORT.label}
            name={fieldName(FTP_PORT)}
            error={getNestErrorMessage(errors, FTP_PORT)}
          />

          <TextInput
            required={ftpIsActive}
            register={register}
            label={FTP_USERNAME.label}
            name={fieldName(FTP_USERNAME)}
            error={getNestErrorMessage(errors, FTP_USERNAME)}
          />

          <TextInput
            type="password"
            required={ftpIsActive}
            register={register}
            label={FTP_PASSWORD.label}
            name={fieldName(FTP_PASSWORD)}
            error={getNestErrorMessage(errors, FTP_PASSWORD)}
          />

          <TextInput
            required={ftpIsActive}
            register={register}
            label={FTP_PATH.label}
            name={fieldName(FTP_PATH)}
            error={getNestErrorMessage(errors, FTP_PATH)}
          />
        </div>

        <div className="my-6 border-t" />

        {/* Schedule - when the automatic run fires and what it covers */}
        <SectionHeader
          title="Schedule"
          description="When this aggregator's automatic run fires and which stores it covers."
        />

        <div className="grid gap-x-5 gap-y-3 mid-md:grid-cols-3">
          <TextInput
            required={ftpIsActive}
            register={register}
            label={FTP_CRON_EXPRESSION.label}
            hintText="Required while FTP is active."
            placeholder="e.g., 0 2 * * *"
            name={fieldName(FTP_CRON_EXPRESSION)}
            error={getNestErrorMessage(errors, FTP_CRON_EXPRESSION)}
          />

          <TextInput
            required={ftpIsActive}
            register={register}
            label={FILE_GENERATION_LIMIT.label}
            hintText="Max files generated concurrently."
            name={fieldName(FILE_GENERATION_LIMIT)}
            error={getNestErrorMessage(errors, FILE_GENERATION_LIMIT)}
          />

          <MultiSelectStoreDropdown
            resetForm
            defaultSelected={aggregator?.ftp?.activatedStores}
            setValue={setValue}
            label={FTP_ACTIVATED_STORES.label}
            hintText="Stores included when the scheduled run fires."
            name={fieldName(FTP_ACTIVATED_STORES)}
            error={getNestErrorMessage(errors, FTP_ACTIVATED_STORES)}
          />
        </div>

        <div className="my-6 border-t" />

        {/* Run monitoring - live status (update only) and the alert limits */}
        <SectionHeader
          title="Run Monitoring"
          description="Status of the scheduled run and the limits that trigger alerts."
        />

        {aggregator ? (
          <div className="mb-5">
            <FtpRunStatus
              ftp={aggregator?.ftp}
              isAggregatorActive={aggregator?.isActive}
            />
          </div>
        ) : (
          <></>
        )}

        <div className="grid gap-x-5 gap-y-3 mid-md:grid-cols-2">
          <TextInput
            register={register}
            label={FTP_MAX_RUNTIME_MINUTES.label}
            hintText="Overrun alert if one run takes longer. Defaults to 120."
            placeholder="120"
            name={fieldName(FTP_MAX_RUNTIME_MINUTES)}
            error={getNestErrorMessage(errors, FTP_MAX_RUNTIME_MINUTES)}
          />

          <TextInput
            register={register}
            label={FTP_MAX_HOURS_WITHOUT_RUN.label}
            hintText="Not-run alert if no run completes in this time. Defaults to 5; set above the longest gap between scheduled runs."
            placeholder="5"
            name={fieldName(FTP_MAX_HOURS_WITHOUT_RUN)}
            error={getNestErrorMessage(errors, FTP_MAX_HOURS_WITHOUT_RUN)}
          />
        </div>

        <div className="my-6 border-t" />

        {/* Profile overrides - per store-group FTP destinations */}
        <FtpProfileOverrides control={control} setValue={setValue} />
      </FormLayout>
    </>
  )
}

export default FtpConfigurationForm
